import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useLinkAtsJob,
  linkAtsJob,
  useImportAtsJob,
  useUnlinkAtsJob,
  useGetAtsStageMappings,
  useUpdateAtsStageMappings,
  getGetJobAtsLinkQueryKey,
} from "@/lib/api/generated";
import type {
  LinkJobRequest,
  ImportJobRequest,
  UpdateStageMappingsRequest,
} from "@/lib/api/model";
import type { AtsStageMapping, AtsStageMappingResponse } from "../types";

/**
 * Link (or resume) a job's ATS connection. On a real (non-dry-run) success,
 * invalidates the link summary + pipeline so the header/candidates reflect
 * the new link immediately. `data.dry_run`/`data.candidate_stage_map` pass
 * straight through — callers (JobLinkDialog) set them per the resume-preview
 * and reactive stage-map flows.
 */
export function useLinkJob(jobId: string) {
  const queryClient = useQueryClient();
  const mutation = useLinkAtsJob({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetJobAtsLinkQueryKey(jobId),
        });
        void queryClient.invalidateQueries({ queryKey: ["pipeline", jobId] });
      },
    },
  });
  return {
    ...mutation,
    mutateAsync: (vars: { data: LinkJobRequest }) =>
      mutation.mutateAsync({ jobId, data: vars.data }),
    mutate: (vars: { data: LinkJobRequest }) =>
      mutation.mutate({ jobId, data: vars.data }),
  };
}

export interface RelinkPreviewView {
  toPull: number;
  toPush: number;
  conflictsRemoteWins: number;
  remoteDeleted: number;
  backfilled: number;
}

/**
 * Dry-run preview of a resume (re-link) — what pulling/pushing/conflict
 * resolution WOULD do, with zero mutations. Implemented as a `useQuery`
 * wrapping the same `linkAtsJob` POST used for real links (`dry_run: true`),
 * not the `useLinkAtsJob` mutation, so a preview never touches the mutation
 * cache or triggers `useLinkJob`'s post-link invalidation — the link stays
 * dormant while previewing (spec: resume dry-run note).
 */
export function useRelinkPreview(
  jobId: string,
  connectionId: string | null,
  remoteJobId: string | null,
  enabled: boolean,
) {
  const query = useQuery({
    queryKey: ["ats-relink-preview", jobId, connectionId, remoteJobId],
    queryFn: () =>
      linkAtsJob(jobId, {
        connection_id: connectionId as string,
        remote_job_id: remoteJobId as string,
        dry_run: true,
      }),
    enabled: enabled && !!connectionId && !!remoteJobId,
  });
  const raw = query.data?.counts;
  const counts: RelinkPreviewView | null = raw
    ? {
        toPull: raw.to_pull ?? 0,
        toPush: raw.to_push ?? 0,
        conflictsRemoteWins: raw.conflicts_remote_wins ?? 0,
        remoteDeleted: raw.remote_deleted ?? 0,
        backfilled: raw.backfilled ?? 0,
      }
    : null;
  return {
    counts,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

/**
 * Unlink a job from its ATS. Wraps the generated DELETE mutation and, on
 * success, invalidates the link summary (so the header flips to the dormant
 * banner) and this job's pipeline (synced candidates freeze). jobId-scoped so
 * call sites don't repeat it per mutate.
 */
export function useUnlinkJob(jobId: string) {
  const queryClient = useQueryClient();
  const mutation = useUnlinkAtsJob({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetJobAtsLinkQueryKey(jobId),
        });
        void queryClient.invalidateQueries({ queryKey: ["pipeline", jobId] });
      },
    },
  });
  return {
    ...mutation,
    mutateAsync: () => mutation.mutateAsync({ jobId }),
    mutate: () => mutation.mutate({ jobId }),
  };
}

export function useImportJob(jobId: string) {
  const mutation = useImportAtsJob();
  return {
    ...mutation,
    mutateAsync: (vars: { data: ImportJobRequest }) =>
      mutation.mutateAsync({ jobId, data: vars.data }),
    mutate: (vars: { data: ImportJobRequest }) =>
      mutation.mutate({ jobId, data: vars.data }),
  };
}

export function useStageMappings(jobId: string | null) {
  const query = useGetAtsStageMappings(jobId ?? "", {
    query: { enabled: !!jobId },
  });
  const raw = query.data ?? null;
  // Memoize the transform: without this, a NEW `data` object is built on every
  // render, so consumers that depend on `data` identity (e.g. the panel's
  // useEffect([data]) that seeds the draft) loop infinitely ("Maximum update
  // depth exceeded"). raw is stable across renders unless the query data changes.
  const data: AtsStageMappingResponse | null = useMemo(
    () =>
      raw
        ? {
        tier: raw.stage_sync_tier as AtsStageMappingResponse["tier"],
        connector_name: raw.connector_name,
        remote_stages: (raw.remote_stages ?? []).map((s) => ({
          remote_stage_id: s.remote_stage_id,
          name: s.name,
        })),
        drift_stages: (raw.drift_stages ?? []).map((s) => ({ name: s.name })),
        mappings: raw.mappings.map(
          (m): AtsStageMapping => ({
            stage_id: m.stage_id,
            stage_name: m.stage_name,
            stage_position: m.stage_position,
            is_system: m.is_system,
            remote_stage_id: m.remote_stage_id ?? null,
            match_source: m.match_source as AtsStageMapping["match_source"],
            is_synced: m.is_synced,
            needs_mapping: m.needs_mapping ?? false,
            archive_resolution: m.archive_resolution ?? null,
            origin_ats_name: m.origin_ats_name ?? null,
          }),
        ),
      }
        : null,
    [raw],
  );
  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useUpdateStageMappings(jobId: string) {
  const mutation = useUpdateAtsStageMappings();
  return {
    ...mutation,
    mutateAsync: (vars: { data: UpdateStageMappingsRequest }) =>
      mutation.mutateAsync({ jobId, data: vars.data }),
    mutate: (vars: { data: UpdateStageMappingsRequest }) =>
      mutation.mutate({ jobId, data: vars.data }),
  };
}
